import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronDown, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import { money } from '@/lib/money'
import type { CustomerRow, OptionGroup, Paginated, Tier } from '@/types'
import Card from '@/components/ui/Card'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { cn } from '@/lib/cn'
import { customerLabel, type CustomerPriceDraft, type Draft, type PatchDraft } from './draft'

/** Prices agreed with one customer, with their own size group prices and option add-ons. */
export default function CustomerPricesSection({
  draft,
  patch,
  groups,
  tiers,
}: {
  draft: Draft
  patch: PatchDraft
  groups: OptionGroup[]
  tiers: Tier[]
}) {
  // Server-side customer search for the agreed-price picker: the endpoint
  // paginates at 15, so without a search only the 15 newest customers would
  // ever be reachable, the same reason the order builder searches.
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerNeedle, setCustomerNeedle] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setCustomerNeedle(customerSearch), 300)
    return () => clearTimeout(handle)
  }, [customerSearch])

  const customers = useQuery({
    queryKey: ['customers-page', '/admin', 'price-picker', customerNeedle],
    queryFn: async () =>
      (
        await api.get<Paginated<CustomerRow>>('/admin/customers', {
          params: { status: 'approved', ...(customerNeedle ? { search: customerNeedle } : {}) },
        })
      ).data.data,
    placeholderData: keepPreviousData,
  })

  const patchCustomer = (index: number, change: Partial<CustomerPriceDraft>) =>
    patch({ customer_prices: draft.customer_prices.map((row, i) => (i === index ? { ...row, ...change } : row)) })

  const setCustomerAddon = (index: number, key: string, value: string, amount: string) => {
    const row = draft.customer_prices[index]
    patchCustomer(index, { addons: { ...row.addons, [key]: { ...(row.addons[key] ?? {}), [value]: amount } } })
  }

  // The tier add-on a customer would pay without their own figure, from the
  // form as typed; a customer with no tier reads as the entry tier.
  const tierAddonFor = (tierId: number | null, key: string, value: string): number => {
    const tier = tierId ?? tiers[0]?.id
    return tier == null ? 0 : Number(draft.addons[key]?.[value]?.[tier]) || 0
  }

  // The visible groups this product offers something in, for a special
  // customer's own add-ons.
  const offeredGroups = groups.filter((group) => (draft.options[group.key] ?? []).length > 0)
  const namedGroups = draft.size_groups.map((group) => group.name.trim()).filter(Boolean)

  return (
    <Card>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {/* SectionLabel's type without its margin: the picker sits on the same row. */}
        <p className="text-base font-medium text-foreground">
          Special customer prices
          {draft.customer_prices.length > 0 && <span className="ml-1 font-normal text-muted">({draft.customer_prices.length})</span>}
        </p>
        <div className="w-full sm:w-64">
          <Select
            value=""
            onChange={(v) => {
              const picked = (customers.data ?? []).find((c) => String(c.id) === v)
              if (!picked) return
              // On top and open, so the admin types its price straight away.
              patch({
                customer_prices: [
                  {
                    user_id: picked.id,
                    label: customerLabel(picked.name, picked.company_name),
                    tierId: picked.tier?.id ?? null,
                    price: '',
                    addons: {},
                    groupPrices: {},
                    showAddons: true,
                  },
                  ...draft.customer_prices.map((row) => ({ ...row, showAddons: false })),
                ],
              })
              setCustomerSearch('')
            }}
            placeholder="Add customer..."
            searchable
            onSearch={setCustomerSearch}
            searchPlaceholder="Search name, phone, company..."
            // Customers already listed are filtered out: one agreed
            // price per customer per product, and a duplicate is refused.
            options={(customers.data ?? [])
              .filter((c) => !draft.customer_prices.some((row) => row.user_id === c.id))
              .map((c) => ({ value: String(c.id), label: customerLabel(c.name, c.company_name) }))}
          />
        </div>
      </div>
      {draft.customer_prices.length === 0 && (
        <p className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-xs text-muted">
          No special prices. Every customer pays their tier price.
        </p>
      )}
      {draft.customer_prices.length > 0 && (
        <div className="divide-y divide-line rounded-lg border border-line">
          {draft.customer_prices.map((row, index) => {
            const setCount = Object.entries(row.addons).reduce(
              (sum, [key, values]) =>
                sum + Object.entries(values).filter(([value, amount]) => amount.trim() !== '' && (draft.options[key] ?? []).includes(value)).length,
              0,
            )
            const groupsSet = namedGroups.filter((name) => (row.groupPrices[name] ?? '').trim() !== '')
            const hasMore = offeredGroups.length > 0 || namedGroups.length > 0
            const summary = [setCount > 0 ? `${setCount} add-ons` : null, ...groupsSet].filter(Boolean).join(' + ')
            // What a blank box falls back to, named by the customer's
            // tier, so the placeholder reads as a price rather than
            // a word. A customer with no tier reads as the entry tier.
            const tier = tiers.find((t) => t.id === row.tierId) ?? tiers[0]
            const tierPrice = tier ? Number(draft.tier_prices[tier.id]) || 0 : 0
            return (
              <div key={row.user_id} className={cn('p-2.5', row.showAddons && 'bg-foreground/[0.02]')}>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 sm:flex">
                  <p className="col-span-2 truncate text-sm font-medium sm:min-w-0 sm:flex-1">{row.label}</p>
                  {hasMore ? (
                    <button
                      type="button"
                      // One open at a time keeps the list short.
                      onClick={() =>
                        patch({
                          customer_prices: draft.customer_prices.map((r, i) => ({ ...r, showAddons: i === index ? !r.showAddons : false })),
                        })
                      }
                      aria-expanded={row.showAddons}
                      className="inline-flex items-center gap-1 justify-self-start whitespace-nowrap text-xs font-semibold text-primary hover:underline sm:w-36"
                    >
                      <ChevronDown className={cn('size-3.5 shrink-0 transition', row.showAddons && 'rotate-180')} />
                      <span className="truncate">{summary || (offeredGroups.length > 0 ? 'Add-ons' : 'Size prices')}</span>
                    </button>
                  ) : (
                    <span />
                  )}
                  <div className="flex items-center gap-2 justify-self-end">
                    <div className="w-24 shrink-0 sm:w-28">
                      <Input
                        type="number"
                        min={0.01}
                        step="0.5"
                        placeholder={tierPrice > 0 ? tierPrice.toFixed(2) : 'Price'}
                        title={tier ? `Leave blank to charge the ${tier.name} price` : undefined}
                        aria-label={`Price for ${row.label}`}
                        value={row.price}
                        onChange={(e) => patchCustomer(index, { price: e.target.value })}
                      />
                    </div>
                    <button
                      type="button"
                      title={`Remove ${row.label}`}
                      onClick={() => patch({ customer_prices: draft.customer_prices.filter((_, i) => i !== index) })}
                      className="shrink-0 rounded-lg border border-line p-2 text-muted transition hover:text-danger"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </div>
                {row.showAddons && (
                  <div className="mt-2 space-y-2.5 border-t border-line pt-2">
                    {draft.size_groups
                      .filter((group) => group.name.trim())
                      .map((group) => {
                        const name = group.name.trim()
                        const tierGroupPrice = tier ? Number(group.prices[tier.id]) || 0 : 0
                        return (
                          <div key={group.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                            <span className="truncate text-xs font-medium">{name} price</span>
                            <div className="flex items-center gap-1.5">
                              <span className="whitespace-nowrap text-2xs text-muted">
                                {tier?.name ?? 'Tier'}: {tierGroupPrice > 0 ? money(tierGroupPrice) : '-'}
                              </span>
                              <div className="w-20 shrink-0 sm:w-24">
                                <Input
                                  type="number"
                                  min={0.01}
                                  step="0.5"
                                  placeholder={tierGroupPrice > 0 ? tierGroupPrice.toFixed(2) : 'Price'}
                                  title={tier ? `Leave blank to charge the ${tier.name} ${name} price` : undefined}
                                  aria-label={`${name} price for ${row.label}`}
                                  value={row.groupPrices[name] ?? ''}
                                  onChange={(e) => patchCustomer(index, { groupPrices: { ...row.groupPrices, [name]: e.target.value } })}
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    {offeredGroups.map((group) => (
                      <div key={group.key}>
                        <p className="mb-1 text-2xs font-semibold uppercase text-muted">{group.label}</p>
                        <div className="grid gap-1.5">
                          {(draft.options[group.key] ?? []).map((value) => {
                            const tierFigure = tierAddonFor(row.tierId, group.key, value)
                            return (
                              <div key={value} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                <span className="truncate text-xs font-medium">{value}</span>
                                <div className="flex items-center gap-1.5">
                                  <span className="whitespace-nowrap text-2xs text-muted">
                                    {tier?.name ?? 'Tier'}: {tierFigure > 0 ? money(tierFigure) : 'free'}
                                  </span>
                                  <div className="w-20 shrink-0 sm:w-24">
                                    <Input
                                      type="number"
                                      min={0}
                                      step="0.5"
                                      placeholder={tierFigure.toFixed(2)}
                                      aria-label={`${value} add-on for ${row.label}`}
                                      value={row.addons[group.key]?.[value] ?? ''}
                                      onChange={(e) => setCustomerAddon(index, group.key, value, e.target.value)}
                                    />
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                    {offeredGroups.length > 0 && (
                      <p className="text-2xs text-muted">
                        RM per piece. Leave blank to charge what {tier?.name ?? 'their tier'} pays; type 0 to make it free
                        for this customer.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <p className="mt-2 text-2xs text-muted">
        A price agreed with one customer, which beats their tier price for this product only; leave it blank to keep
        their tier price. Option add-ons follow their tier unless set here. Name and number, and urgent charges still
        apply on top. Leave this empty for everyone else.
      </p>
    </Card>
  )
}
